import React, { Component } from 'react';

import { getCountryCallingCode, isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import memoizeOne from 'memoize-one';

import { PhoneNumberAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { bool, eventListener, expression, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { LocaleService } from '@philips-emr/tws-core/lib/services';
import { classNames, defaultClasses, isFunction } from '@philips-emr/tws-utils';

import { Input } from '..';

import { CountrySelect } from './components';
import './phone-number.scss';

const noop = () => {};

const parsePhone = memoizeOne((value = '', defaultCountry = '', safe = true) => {
  const phone = parsePhoneNumberFromString(value, defaultCountry);

  if (!safe && !phone) return;

  const safePhone = phone || {};
  const country = safePhone.country || defaultCountry;
  const countryCallingCode = isSupportedCountry(country) ? getCountryCallingCode(country) : '';
  const nationalNumber = safePhone.nationalNumber || value.replace(`+${countryCallingCode}`, '');
  const formattedNational = (safePhone.formatNational && safePhone.formatNational()) || nationalNumber;
  const prefixedCallingCode = countryCallingCode ? `+${countryCallingCode}` : '';

  return {
    country,
    countryCallingCode,
    formattedNational,
    nationalNumber,
    get number() {
      return nationalNumber
        ? `${prefixedCallingCode}${nationalNumber}`
        : undefined;
    },
  };
});

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: PhoneNumberAPI })
class PhoneNumber extends Component {
  static propTypes = {
    enabled: bool,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: expression,
    readOnly: bool,
    valid: bool,
    value: string,
  };

  static defaultProps = {
    enabled: true,
  };

  state = {
    country: LocaleService.getCurrentLocale(),
    enabled: true,
    focused: false,
  };

  componentDidMount() {
    const { value } = this.props;
    const { country } = this.state;

    const phone = parsePhone(value, country, false);

    if (phone) {
      this.setState({
        country: phone.country,
        number: phone.nationalNumber,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { country, number } = this.state;
    const { value } = this.props;
    const { value: oldValue } = prevProps;

    if (value && value !== oldValue) {
      const statePhone = parsePhone(number, country);
      const propsPhone = parsePhone(value, country);

      if (this.match(propsPhone, statePhone)) {
        return;
      }

      this.setState({
        country: propsPhone.country,
        number: propsPhone.nationalNumber,
      });
    } else if (oldValue && !value) {
      this.setState({
        number: undefined,
      });
    }
  }

  match(phoneA, phoneB) {
    return phoneA.country === phoneB.country
      && phoneA.nationalNumber === phoneB.nationalNumber;
  }

  getParsedPhone() {
    const { country, number } = this.state;

    return parsePhone(number, country);
  }

  getValue() {
    const { focused } = this.state;
    const phone = this.getParsedPhone();

    return focused ? phone.nationalNumber : phone.formattedNational;
  }

  onFocus = () => {
    const { onFocus = noop } = this.props;

    this.setState({ focused: true }, onFocus);
  };

  onBlur = () => {
    const { onBlur = noop } = this.props;

    this.setState({ focused: false }, onBlur);
  };

  onChange = () => {
    const { value: oldValue, onChange } = this.props;
    const { country, number } = this.state;

    const phone = parsePhone(number, country);

    isFunction(onChange) && onChange({
      value: phone.number,
      oldValue,
    });
  };

  onCountryChange = ({ code }) => {
    this.setState({ country: code }, this.onChange);
  };

  onNumberChange = (e) => {
    this.setState({ number: e.value }, this.onChange);
  };

  render() {
    const { readOnly, enabled, onKeyPress, onKeyDown, onKeyUp, placeholder, valid } = this.props;
    const { country } = this.state;

    const containerClasses = classNames({
      'c-phone-number': true,
      'c-phone-number--invalid': !valid,
      ...defaultClasses(this.props),
    });

    return (
      <div className={containerClasses}>
        <CountrySelect enabled={enabled} readOnly={readOnly} value={country} onChange={this.onCountryChange} />
        <Input
          className="c-phone-number__input"
          value={this.getValue()}
          enabled={enabled}
          readOnly={readOnly}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onChange={this.onNumberChange}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
        />
      </div>
    );
  }
}

export default PhoneNumber;
